import React from 'react';
import { Tooltip } from 'antd';
import { CalendarOutlined, DollarOutlined, LineChartOutlined, ShopOutlined, UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatCurrencyLocalized } from '../../../utils/currency';
import { formatLocalizedPercent } from '../../../utils/numberFormatting';
import { getOrderProcessStepCopyKey, type OrderProcessCopyFacts, type OrderProcessStepKey } from './orderProcessSteps';
import { ORDER_MARGIN_STAGE_LABEL_KEYS, type OrderMarginFigure, type OrderMarginVisibility } from './orderMarginStage';

export type OrderMarginPending = 'new' | 'stale';

interface OrderProcessHeaderProps {
  orderNumber: React.ReactNode;
  /** The step whose title and description the header shows (the viewed one). */
  viewedStep: OrderProcessStepKey;
  /**
   * Steps 1–3 follow the first mockup: the step card (`process/guide`) carries
   * the title and the help text, so the header keeps only the number and the
   * status pill, as the mockup's header does.
   */
  hideStepCopy?: boolean;
  pillKey: string;
  pillTone: 'default' | 'done' | 'warning';
  customerName: string;
  createdAt: string;
  orderDate?: React.ReactNode;
  currency: string;
  accountManagerName: string;
  margin: OrderMarginFigure | null;
  marginVisibility: OrderMarginVisibility;
  /**
   * Szkic only: the margin plan is computed by the server from SAVED lines.
   * `new` = the card has no saved lines yet (placeholder chip, no number);
   * `stale` = the lines are edited and not saved (the chip shows the last
   * saved plan and says so). Nothing is computed on the page.
   */
  marginPending?: OrderMarginPending;
  /** Back to the order list („Zamówienia”), shown on the left before the number. */
  back?: React.ReactNode;
  /** Kept header actions (PDF, e-mails, number, delivery revert) — usually one „Więcej” menu. */
  actions?: React.ReactNode;
  /** Alerts that belong to the whole order (archived, blocked delete, quote). */
  alerts?: React.ReactNode;
  /** Wording facts of the step texts (self-pickup, all in stock, confirmation sent). */
  copy?: Partial<OrderProcessCopyFacts>;
}

/**
 * „bez kosztu: A (powód), B” / „niepełny koszt: A, B” — the lines that make
 * the margin unknown or incomplete, one sentence for the chip and the tiles.
 */
export function useMarginIncompleteText(margin: OrderMarginFigure): string | null {
  const { t } = useTranslation(['orders']);
  if (margin.costState === 'KNOWN') return null;
  const lines = margin.incompleteLines
    .map((line) => (line.reason ? `${line.name} (${t(`orders:warehouseFlow.pendingReason.${line.reason}`)})` : line.name))
    .join(', ');
  const key = margin.costState === 'UNKNOWN' ? 'orders:process.margin.unknownLines' : 'orders:process.margin.partialLines';
  return lines ? t(key, { lines }) : t(margin.costState === 'UNKNOWN' ? 'orders:process.margin.unknownHint' : 'orders:process.margin.incompleteHint');
}

/**
 * Margin chip of the header: label from the stage, value from one source,
 * never recomputed. Unknown cost reads „koszt nieznany” (no amount, no %);
 * an incomplete cost shows the known part with „niepełny koszt”, never green.
 */
export function OrderMarginChip({ margin, visibility, stale = false }: {
  margin: OrderMarginFigure;
  visibility: OrderMarginVisibility;
  /** Unsaved line edits: the figure is the last saved plan. */
  stale?: boolean;
}) {
  const { t, i18n } = useTranslation(['orders']);
  const incompleteText = useMarginIncompleteText(margin);
  if (!visibility.showMargin) return null;
  const percent = margin.percent == null ? null : formatLocalizedPercent(margin.percent, i18n.language);
  const amount = visibility.showAmount && margin.amount != null
    ? formatCurrencyLocalized(margin.amount, 'PLN', i18n.language)
    : null;
  const figures = margin.costState === 'UNKNOWN' ? [] : [amount, percent].filter((part): part is string => Boolean(part));
  // Amount and percent are never cut (staging v3.27.0 at 1024 px: „300,00 zł · 60,0…”):
  // each stays whole and the percent moves under the amount when the tile is narrow.
  const value = figures.length > 0 ? (
    <span className="zk-chip__text zk-chip__text--figures">
      {figures.map((part, index) => (
        <React.Fragment key={part}>
          {index > 0 ? ' ' : null}
          <span className="zk-chip__figure">{index > 0 ? `· ${part}` : part}</span>
        </React.Fragment>
      ))}
    </span>
  ) : <span className="zk-chip__text">{t('orders:process.margin.unknown')}</span>;
  const loss = margin.costState !== 'UNKNOWN' && margin.amount != null && margin.amount < 0;
  const tone = loss ? ' zk-chip--loss' : margin.costState === 'KNOWN' ? '' : ' zk-chip--incomplete';
  return (
    <Tooltip title={incompleteText || t(`orders:process.margin.explain.${margin.stage}`)}>
      <div
        className={`zk-chip zk-chip--margin${tone}${stale ? ' zk-chip--stale' : ''}`}
        data-stale={stale ? 'true' : 'false'}
        data-testid="zk-margin-chip"
        data-stage={margin.stage}
        data-cost-state={margin.costState}
        data-provisional={margin.provisional ? 'true' : 'false'}
      >
        <span className="zk-chip__label">
          {t(ORDER_MARGIN_STAGE_LABEL_KEYS[margin.stage])}
          {margin.provisional && ` · ${t('orders:process.margin.provisionalShort')}`}
        </span>
        <span className="zk-chip__value"><LineChartOutlined aria-hidden="true" />{value}</span>
        {stale && <span className="zk-chip__note zk-chip__note--stale" data-testid="zk-margin-stale">{t('orders:process.margin.staleUntilSave')}</span>}
        {/* Short on the chip; the lines it concerns are in the tooltip and in the margin card. */}
        {incompleteText && <span className="zk-chip__note" data-testid="zk-margin-incomplete-lines">{t('orders:process.margin.chipFillCost')}</span>}
        {margin.provisional && <span className="zk-chip__note" data-testid="zk-margin-provisional">{t('orders:process.margin.provisional')}</span>}
      </div>
    </Tooltip>
  );
}

/**
 * New, not yet saved Szkic: the margin plan needs saved lines (the server
 * computes it — FIFO in confirmation order, average purchase cost for the
 * shortfall), so the chip of the mockup is there with its place and the way
 * to get the number.
 */
export function OrderMarginPlaceholderChip({ visibility }: { visibility: OrderMarginVisibility }) {
  const { t } = useTranslation(['orders']);
  if (!visibility.showMargin) return null;
  return (
    <Tooltip title={t('orders:process.margin.afterSaveHint')}>
      <div className="zk-chip zk-chip--margin zk-chip--pending" data-testid="zk-margin-chip" data-stage="PLANNED" data-cost-state="PENDING_SAVE">
        <span className="zk-chip__label">{t(ORDER_MARGIN_STAGE_LABEL_KEYS.PLANNED)}</span>
        <span className="zk-chip__value"><LineChartOutlined aria-hidden="true" /><span className="zk-chip__text">{t('orders:process.margin.afterSave')}</span></span>
      </div>
    </Tooltip>
  );
}

/**
 * Header card of the ZK card (makieta `head()`): „Zamówienie od klienta …”,
 * status pill, the title and description of the viewed step, and the chips
 * Klient / Data zamówienia / Data utworzenia / Waluta / Opiekun handlowy / margin.
 */
export default function OrderProcessHeader({
  orderNumber,
  viewedStep,
  hideStepCopy = false,
  pillKey,
  pillTone,
  customerName,
  createdAt,
  orderDate,
  currency,
  accountManagerName,
  margin,
  marginVisibility,
  marginPending,
  back,
  actions,
  alerts,
  copy = {},
}: OrderProcessHeaderProps) {
  const { t } = useTranslation(['orders']);
  const chip = (label: string, icon: React.ReactNode, value: React.ReactNode, testId?: string) => (
    <div className="zk-chip" data-testid={testId}>
      <span className="zk-chip__label">{label}</span>
      <span className="zk-chip__value">
        {icon}
        <span className="zk-chip__text" title={typeof value === 'string' ? value : undefined}>{value}</span>
      </span>
    </div>
  );
  return (
    <section className="zk-section zk-head" data-testid="zk-header">
      <div className="zk-head__main">
        <div className="zk-head__tags">
          {back}
          <span className="zk-tag">{t('orders:process.tag')} <span className="zk-head__number">{orderNumber}</span></span>
          <span className={`zk-pill${pillTone === 'default' ? '' : ` zk-pill--${pillTone}`}`} data-testid="zk-status-pill">{t(pillKey)}</span>
        </div>
        {!hideStepCopy && (
          <>
            <h2 className="zk-head__title">{t(getOrderProcessStepCopyKey(viewedStep, 'title', copy))}</h2>
            <p className="zk-head__description">{t(getOrderProcessStepCopyKey(viewedStep, 'description', copy))}</p>
          </>
        )}
      </div>
      {actions && <div className="zk-head__actions">{actions}</div>}
      <div className="zk-chips">
        {chip(t('orders:process.chips.customer'), <ShopOutlined aria-hidden="true" />, customerName)}
        {orderDate && chip(t('orders:orderDetail.orderDate'), <CalendarOutlined aria-hidden="true" />, orderDate)}
        {chip(t('orders:process.chips.createdAt'), <CalendarOutlined aria-hidden="true" />, createdAt)}
        {chip(t('orders:process.chips.currency'), <DollarOutlined aria-hidden="true" />, currency)}
        {chip(t('orders:process.chips.accountManager'), <UserOutlined aria-hidden="true" />, accountManagerName)}
        {margin && marginPending !== 'new' && <OrderMarginChip margin={margin} visibility={marginVisibility} stale={marginPending === 'stale'} />}
        {marginPending === 'new' && <OrderMarginPlaceholderChip visibility={marginVisibility} />}
      </div>
      {alerts && <div className="zk-head__alerts">{alerts}</div>}
    </section>
  );
}
